import { useLanguage } from "@hooks/useLanguage";
import { useBaseData } from "@hooks/useBaseData";
import { BASE_DATA } from "@constants/base-data";
import {
  getConvertedUnitData,
  getConvertedOfficeAddress,
  getConvertedPositionData,
} from "@helpers/baseDataConvert";
import { useFormContext } from "react-hook-form";
import { useEmployeesForReset } from "@hooks/HRM/employees/useEmployeesForReset";
import { SelectControllerRow } from "./SelectControllerRow";
import { SectionHeader } from "@pentabd/ui";
import { useTranslation } from "react-i18next";
import { getEmployeeServiceDataConvertForProcurement } from "@helpers/employeeServiceDataConvert";
import { useUnitsByOfficeId } from "@hooks/HRM/settings/useUnitsByOfficeId";
import { useDesignationsByOffice } from "@hooks/HRM/settings/useDesignationsByOffice";

const { OFFICE } = BASE_DATA;

type SelectEmployeeFromOfficeProps = {
  getSelectedEmployeeId: (id: number | undefined) => void;
};

export const FIELD_NAMES_OF_EMPLOYEE_SELECT = {
  officeId: "officeId",
  unitId: "unitId",
  positionId: "positionId",
  employeeId: "employeeId",
};

export default function SelectEmployeeFromOffice({
  getSelectedEmployeeId,
}: SelectEmployeeFromOfficeProps) {
  const { language } = useLanguage();
  const { t } = useTranslation();
  const { control, register, setValue, watch } = useFormContext();
  const { handleQuery, employees } = useEmployeesForReset();

  const { data: office } = useBaseData(OFFICE);
  const { units, handleQuery: handleUnitsQuery } = useUnitsByOfficeId();
  const { designations: positions, handleQuery: handleDesignationsQuery } =
    useDesignationsByOffice();

  const officeId = watch(FIELD_NAMES_OF_EMPLOYEE_SELECT.officeId);
  const positionId = watch(FIELD_NAMES_OF_EMPLOYEE_SELECT.positionId);
  const unitId = watch(FIELD_NAMES_OF_EMPLOYEE_SELECT.unitId);

  return (
    <div>
      <SectionHeader title={t("HRM_SETTINGS.SELECT_EMPLOYEE.HEADER")} />
      <div className="p-10 d-flex flex-column gap-8">
        <SelectControllerRow
          control={control}
          label="PROMOTION_CANDIDATES.TABLE.FORM.OFFICE"
          name={FIELD_NAMES_OF_EMPLOYEE_SELECT.officeId}
          options={getConvertedOfficeAddress(office, language)}
          register={register}
          setValue={setValue}
          isSearchable
          selectAction={(e) => {
            handleUnitsQuery(
              FIELD_NAMES_OF_EMPLOYEE_SELECT.officeId,
              e as number,
            );
            setValue(FIELD_NAMES_OF_EMPLOYEE_SELECT.unitId, undefined);
            setValue(FIELD_NAMES_OF_EMPLOYEE_SELECT.positionId, undefined);
            setValue(FIELD_NAMES_OF_EMPLOYEE_SELECT.employeeId, undefined);
            getSelectedEmployeeId(undefined);
          }}
          portal
        />

        <SelectControllerRow
          control={control}
          label="PROMOTION_CANDIDATES.TABLE.FORM.UNIT"
          name={FIELD_NAMES_OF_EMPLOYEE_SELECT.unitId}
          options={getConvertedUnitData(
            units.filter((item: any) => item?.name !== "ACC"),
            language,
          )}
          register={register}
          setValue={setValue}
          isSearchable
          selectAction={(e) => {
            handleDesignationsQuery(
              FIELD_NAMES_OF_EMPLOYEE_SELECT.unitId,
              e as number,
            );
            handleDesignationsQuery(
              FIELD_NAMES_OF_EMPLOYEE_SELECT.officeId,
              officeId,
            );
            setValue(FIELD_NAMES_OF_EMPLOYEE_SELECT.positionId, undefined);
            setValue(FIELD_NAMES_OF_EMPLOYEE_SELECT.employeeId, undefined);
            getSelectedEmployeeId(undefined);
          }}
          portal
          disabled={!officeId}
        />

        <SelectControllerRow
          control={control}
          label="PROMOTION_CANDIDATES.TABLE.FORM.DESIGNATION"
          name={FIELD_NAMES_OF_EMPLOYEE_SELECT.positionId}
          options={getConvertedPositionData(positions, language)}
          register={register}
          setValue={setValue}
          isSearchable
          selectAction={(e) => {
            handleQuery({
              positionId: e as number,
              officeId: officeId,
              unitId: unitId,
            });
            setValue(FIELD_NAMES_OF_EMPLOYEE_SELECT.employeeId, undefined);
            getSelectedEmployeeId(undefined);
          }}
          portal
          disabled={!unitId}
        />

        <SelectControllerRow
          control={control}
          label="HRM_SETTINGS.SELECT_EMPLOYEE.SELECT_EMPLOYEE"
          name={FIELD_NAMES_OF_EMPLOYEE_SELECT.employeeId}
          options={getEmployeeServiceDataConvertForProcurement(employees)}
          register={register(FIELD_NAMES_OF_EMPLOYEE_SELECT.employeeId)}
          setValue={setValue}
          portal
          selectAction={(e) => {
            getSelectedEmployeeId(e as number);
          }}
          disabled={!positionId}
        />
      </div>
    </div>
  );
}
